import { useState, useEffect, useCallback, useMemo } from 'react';
import * as _ from 'lodash';
import { selectOptionFactory } from '../../../../utils';
import type { JiraProjectSettings } from './jira/JiraTicketExportContent';
import type { SelectOption } from '../../../../types';
// TODO: Use a shared type
// eslint-disable-next-line import/no-restricted-paths
import type { Jira } from '../../../../../models/types';
import type { JiraCustomField, IssueExportRequest } from '../../../../../shared/jira';
import { Category } from '../../../../../shared/issuesV2';

//  The jira-related export bundle.
export type JiraExportOptions = Partial<
  Pick<IssueExportRequest, 'jira_id' | 'project_id' | 'issueType' | 'labels' | 'customFields' | 'components'>
>;

type JiraExportFieldBase = {
  id: string;
  label: string;
};

export type JiraExportFieldSelect = JiraExportFieldBase & {
  type: 'select';
  value: SelectOption<string>;
  options: Array<SelectOption<string>>;
};

export type JiraExportFieldMultiSelect = JiraExportFieldBase & {
  type: 'multi-select';
  value: Array<SelectOption<string>>;
  options: Array<SelectOption<string | Record<string, string>>>;
};

export type JiraExportFieldInput = JiraExportFieldBase & {
  type: 'input';
  value: string;
};

type ExportField =
  | JiraExportFieldSelect
  | JiraExportFieldMultiSelect
  | JiraExportFieldInput;

export type WithOnChange<T> = T & {
  onChange: (change: Pick<ExportField, 'id' | 'value'>) => void;
};

export type ExportFieldWithOnChange = WithOnChange<ExportField>;

type JiraExportFields = {
  required: ExportField[];
  optional: ExportField[];
};

export type JiraExportFieldsWithOnChange = {
  required: ExportFieldWithOnChange[];
  optional: ExportFieldWithOnChange[];
};

//  Helper to check if a customField is required.
const isCustomFieldRequired = (customField: JiraCustomField) => customField.isRequired;

//  Checks if an export field ID is numeric, meaning it is a customField
//  All pre-determined fields (such as labels) have string/non-numeric IDs such as "labels".
const isCustomFieldId = (id: string): boolean => Number.isFinite(Number(id));

//  Helper to map a JiraCustomField to a ExportField.
const customFieldToExportField = ({
  displayName,
  fieldId,
  value,
}: JiraCustomField & { value?: string }): ExportField => ({
  id: fieldId,
  type: 'input',
  label: displayName,
  value: value || '',
});

//  Helper for converting custom fields into export fields
const convertCustomFieldsToExportFields = ({
  customFields,
  exportOptions,
  isRequired,
}: {
  customFields: Record<string, JiraCustomField>;
  exportOptions: JiraExportOptions;
  isRequired: boolean;
}): ExportField[] => Object.values(customFields)
  .filter(field => isRequired ? isCustomFieldRequired(field) : !isCustomFieldRequired(field))
  .map(field => {
    const exportedCustomField = exportOptions.customFields?.find(({ fieldId }) => fieldId === field.fieldId);
    return !exportedCustomField ? customFieldToExportField(field) : customFieldToExportField({
      ...field,
      //  Ensure we use the up-to-date value
      value: exportedCustomField.value,
    });
  });

/**
 * Takes the selected Jira and sets some default values.
 *
 * @param {Partial<Jira>} jira - Current selectedJira
 * @param {JiraProjectSettings} projectSettings - Project Jira settings.
 * @param {Category} issueCategory - the issue type in order to determine default project id
 */
function getExportOptionsFromJira(
  jira: Partial<Jira> | undefined,
  projectSettings: JiraProjectSettings,
  issueCategory: Category,
): JiraExportOptions {
  if (!jira) {
    return {} as JiraExportOptions;
  }

  if (projectSettings.useGlobalTrackerSettings) {
    let defaultProjectId;
    if (issueCategory === Category.Licensing && jira.defaultLicensingProject) {
      defaultProjectId = jira.defaultLicensingProject;
    } else if (issueCategory === Category.Vulnerability && jira.defaultSecurityProject) {
      defaultProjectId = jira.defaultSecurityProject;
    }

    return {
      jira_id: jira.id,
      project_id: defaultProjectId || _.head(jira?.jiraProjectIds) || undefined,
      issueType: _.head(jira?.issueTypes) || undefined,
      labels: jira?.labels ?? undefined,
      customFields: jira?.customFields
        ? Object.values(jira.customFields).map(({ fieldId, defaultValue }) => ({ fieldId, value: defaultValue || '' }))
        : undefined,
      components: jira?.components ? Object.values(jira.components).map(({ id }) => (id)) : [],
    };
  }

  return {
    jira_id: jira.id,
    project_id: _.head(projectSettings?.issueTrackerProjectIds) || undefined,
    issueType: _.head(projectSettings?.issueTrackerIssueTypes) || undefined,
    labels: projectSettings?.issueTrackerLabels ?? undefined,
    customFields: projectSettings?.issueTrackerCustomFields
      ? Object.values(projectSettings?.issueTrackerCustomFields).map(({ fieldId, defaultValue }) => ({
        fieldId,
        value: defaultValue || '',
      }))
      : undefined,
    components: projectSettings?.issueTrackerComponents
      ? Object.values(projectSettings.issueTrackerComponents).map(({ id }) => (id))
      : [],
  };
}

/**
 * Returns the required fields for jira export.
 * The required fields are:
 * - site (not included here as it's state is handled spearately),
 * - projectId - The jira project ID,
 * - any required customFields,
 *
 * @param {Partial<Jira>} jira - The selected Jira object.
 */
function getRequiredFields(
  { jiraProjectIds, customFields }: Partial<Jira>,
  exportOptions: JiraExportOptions,
  projectSettings: JiraProjectSettings,
): ExportField[] {
  const exportFields: ExportField[] = [];

  if (
    jiraProjectIds?.length ||
    (!projectSettings.useGlobalTrackerSettings && projectSettings?.issueTrackerProjectIds?.length)
  ) {
    const projectIds = projectSettings.useGlobalTrackerSettings
      ? jiraProjectIds
      : projectSettings.issueTrackerProjectIds;

    const currentProjectId = exportOptions?.project_id || _.head(projectIds) as string;
    if (currentProjectId) {
      exportFields.push({
        id: 'project_id',
        type: 'select',
        label: 'Project Key',
        value: selectOptionFactory(currentProjectId),
        options: (projectIds as string[]).map(projectId => selectOptionFactory(projectId)),
      });
    }
  } else {
    //  There needs to be a Jira project ID when exporting to Jira.
    //  If for some reason they do not have a project ID, we let them enter one manually.
    const currentProjectId = exportOptions?.project_id || '';

    exportFields.push({
      id: 'project_id',
      type: 'input',
      label: 'Project Key',
      value: currentProjectId,
    });
  }

  if (projectSettings.useGlobalTrackerSettings) {
    if (customFields) {
      return [
        ...exportFields,
        ...convertCustomFieldsToExportFields({ customFields, exportOptions, isRequired: true }),
      ];
    }
  } else if (projectSettings.issueTrackerCustomFields) {
    return [
      ...exportFields,
      ...convertCustomFieldsToExportFields({
        customFields: projectSettings.issueTrackerCustomFields,
        exportOptions,
        isRequired: true,
      }),
    ];
  }

  return exportFields;
}

/**
 * Returns the optional fields for jira export.
 * The optional fields are:
 * - labels,
 * - issueType
 * - any optional customFields
 * - components, if any are configured.
 *
 * @param {Partial<Jira>} jira - The selected Jira object.
 */
function getOptionalFields(
  { labels, issueTypes, customFields, components }: Partial<Jira>,
  exportOptions: JiraExportOptions,
  projectSettings: JiraProjectSettings,
): ExportField[] {
  const exportFields: ExportField[] = [];

  if (labels?.length || projectSettings.issueTrackerLabels?.length) {
    const currentLabels = projectSettings.useGlobalTrackerSettings
      ? labels
      : projectSettings.issueTrackerLabels;
    const exportLabels = exportOptions?.labels ?? null;
    //  The only way this is falsey is if they are using project settings but haven't configured them properly (user error).
    if (exportLabels) {
      exportFields.push({
        id: 'labels',
        type: 'multi-select',
        label: 'Label(s)',
        value: exportLabels ? exportLabels.map(label => selectOptionFactory(label)) : [],
        options: (currentLabels as string[]).map(label => selectOptionFactory(label)),
      });
    }
  }

  if (issueTypes?.length || projectSettings.issueTrackerIssueTypes?.length) {
    const currentIssueTypes = projectSettings.useGlobalTrackerSettings
      ? issueTypes
      : projectSettings.issueTrackerIssueTypes;
    const exportIssueType = exportOptions?.issueType ?? null;
    //  The only way this is falsey is if they are using project settings but haven't configured them properly (user error).
    if (exportIssueType) {
      exportFields.push({
        id: 'issueType',
        type: 'select',
        label: 'Issue Type',
        value: selectOptionFactory(exportIssueType || _.head(currentIssueTypes) as string),
        options: (currentIssueTypes as string[]).map(issueType => selectOptionFactory(issueType)),
      });
    }
  }

  if (components && !_.isEmpty(components)) {
    const currentComponents = Object.values(components);
    const exportComponents = exportOptions?.components ?? [];
    if (exportComponents) {
      const exportLabelMap: Record<string, string> = currentComponents.reduce(
        (acc, { id, displayName }) => ({ ...acc, [id]: displayName }),
        {},
      );
      exportFields.push({
        id: 'components',
        type: 'multi-select',
        label: 'Component(s)',
        value: exportComponents
          ? exportComponents.map((id) => selectOptionFactory(id, exportLabelMap[id]))
          : [],
        options: currentComponents.map(({ id, displayName }) =>
          selectOptionFactory(id, displayName),
        ),
      });
    }
  }

  if (projectSettings.useGlobalTrackerSettings) {
    if (customFields) {
      return [
        ...exportFields,
        ...convertCustomFieldsToExportFields({ customFields, exportOptions, isRequired: false }),
      ];
    }
  } else if (projectSettings.issueTrackerCustomFields) {
    return [
      ...exportFields,
      ...convertCustomFieldsToExportFields({
        customFields: projectSettings.issueTrackerCustomFields,
        exportOptions,
        isRequired: false,
      }),
    ];
  }

  return exportFields;
}

/**
 * Takes the selected Jira, then normalizes and groups
 * the fields by required/optional.
 * Takes the exportOptions which stores the current values for each field.
 *
 * @param {Partial<Jira>} jira - The currently selected Jira
 * @param {JiraExportOptions} exportOptions - Current values for the fields.
 * @param {JiraProjectSettings} projectSettings - Project settings.
 * @returns {JiraExportFields} - required and optional fields to be returned
 */
function getFieldsFromJira(
  jira: Partial<Jira> | undefined,
  exportOptions: JiraExportOptions,
  projectSettings: JiraProjectSettings,
): JiraExportFields {
  if (!jira) {
    return {
      required: [],
      optional: [],
    };
  }

  return {
    required: getRequiredFields(jira, exportOptions, projectSettings),
    optional: getOptionalFields(jira, exportOptions, projectSettings),
  };
}

export type UseJiraExportProps = {
  jiras: Array<Partial<Jira>>;
  issueCategory: Category;
  projectSettings: JiraProjectSettings;
};

type UseJiraExportReturn = JiraExportFieldsWithOnChange & {
  baseURL: string;
  exportOptions: JiraExportOptions;
};

/**
 * Hook for managing the jira-related fields for the ModalIssueExport state.
 */
const useJiraExport = ({ jiras, issueCategory, projectSettings }: UseJiraExportProps): UseJiraExportReturn => {
  const [selectedJira, setSelectedJira] = useState<Partial<Jira> | undefined>(_.head(jiras));
  const [exportOptions, setExportOptions] = useState<JiraExportOptions>(
    getExportOptionsFromJira(selectedJira, projectSettings, issueCategory),
  );
  const [jiraExportFields, setJiraExportFields] = useState<JiraExportFields>(
    getFieldsFromJira(selectedJira, exportOptions, projectSettings),
  );

  //  Update the fields when you select a different Jira.
  //  Note that this will clear away previous selections if the user returns to this Jira.
  useEffect(() => {
    setExportOptions(() => {
      const nextExportOptions: JiraExportOptions = getExportOptionsFromJira(
        selectedJira,
        projectSettings,
        issueCategory,
      );
      const nextFields = getFieldsFromJira(selectedJira, nextExportOptions, projectSettings);
      setJiraExportFields(nextFields);
      return nextExportOptions;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jiras, selectedJira?.id, projectSettings.useGlobalTrackerSettings]);

  const onChangeValue = useCallback((change: Pick<ExportField, 'id' | 'value'>) =>
    setExportOptions((currentExportOptions: JiraExportOptions) => {
      const nextExportOptions: JiraExportOptions = { ...currentExportOptions };
      //  If the fieldId is a number, this is a custom field.
      if (isCustomFieldId(change.id)) {
        nextExportOptions.customFields = (nextExportOptions?.customFields || [])
          //  Iterate through the fields and replace the value of the one which exists here.
          ?.map(field => field.fieldId === change.id
            ? ({ ...field, value: change.value })
            : field,
          );
      } else {
        nextExportOptions[change.id] = change.value;
      }
      setJiraExportFields(getFieldsFromJira(selectedJira, nextExportOptions, projectSettings));
      return nextExportOptions;
    }), [setJiraExportFields, selectedJira, projectSettings]);

  //  The site field is slightly special, because the rest of the fields are pulled from it.
  const siteField: WithOnChange<JiraExportFieldSelect> = useMemo(() => !selectedJira ? null : ({
    label: 'Site',
    type: 'select',
    value: selectOptionFactory(selectedJira?.id as unknown as string, selectedJira?.name as string),
    options: jiras.map(jira =>
      selectOptionFactory(jira.id as unknown as string, jira.name)) as Array<SelectOption<string>>,
    onChange: ({ value: id }: { value: number }) =>
      setSelectedJira(jiras.find(jira => jira.id === id) as Partial<Jira>),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [jiras, selectedJira?.id, selectedJira?.name]);

  //  Method which will apply the change handler to each field.
  const applyOnChange = useCallback((field: ExportField): ExportFieldWithOnChange => {
    return {
      ...field,
      onChange: onChangeValue,
    };
  }, [onChangeValue]);

  return {
    baseURL: selectedJira?.base_url as string,
    exportOptions,
    required: [siteField, ...jiraExportFields.required.map(applyOnChange)].filter(field => field),
    optional: jiraExportFields.optional.map(applyOnChange),
  };
};

export default useJiraExport;
